import type { ReportRequest } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Form,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react"
import type { FormEventHandler } from "react"

import { DateRangeInput } from "./DateRangeInput"
import { EmailInput } from "./EmailInput"
import styles from "./Reports.module.scss"
import { ShowPhotosToggle } from "./ShowPhotosToggle"
import { useSendReport } from "./useSendReport"
import { PageTemplate } from "src/components"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import { useAccountProperty } from "src/hooks"
import {
  SendReportErrorToast,
  SendReportSuccessToast,
} from "src/toasts"
import {
  injectProps,
  getSugarmateError,
  followToSharer,
} from "src/utils"

const polyglotPrefix = "pages.settings.generate_report."

export function Reports() {
  const polyglot = usePolyglot()

  const {
    currentUserId,
    currentFollow,
  } = useFollowedUsers()

  const email = useAccountProperty(
    "email",
    "",
  )
  const sendReport = useSendReport()
  const { showToast } = useToasterContext()

  const [
    sending,
    setSending,
  ] = useState<boolean>(false)

  const currentReportRequestRef = useRef<ReportRequest>({
    email,
    start_date: "",
    end_date: "",
    show_photos: false,
  })

  const [
    currentReportRequest,
    setCurrentReportRequest,
  ] = useState<ReportRequest>(
    currentReportRequestRef.current,
  )

  const analyticsRef = useSegment()

  const updateCurrentReportRequest = useCallback(
    (report: Partial<ReportRequest>) => {
      currentReportRequestRef.current = {
        ...currentReportRequestRef.current,
        ...report,
      }

      setCurrentReportRequest(currentReportRequestRef.current)
    },
    [
      currentReportRequestRef,
      setCurrentReportRequest,
    ],
  )

  // if the email comes in and the email field is currently blank, update it
  useEffect(
    () => {
      if (!!email && !currentReportRequestRef.current.email) {
        updateCurrentReportRequest({
          email,
        })
      }
    },
    [
      email,
      currentReportRequestRef,
      updateCurrentReportRequest,
    ],
  )

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (ev) => {
      ev.preventDefault()
      ev.stopPropagation()

      void (async () => {
        setSending(true)

        try {
          await sendReport({
            email: currentReportRequest.email,
            start_date: currentReportRequest.start_date,
            end_date: currentReportRequest.end_date,
            show_photos: currentReportRequest.show_photos,
            follow_id: currentFollow.id,
          })

          void analyticsRef.current.track("Stat Report Sent")

          const SuccessToast = injectProps(
            SendReportSuccessToast,
            {
              email: currentReportRequest.email,
              followName: currentFollow.is_self ? null : followToSharer(currentFollow),
            },
          )

          showToast(
            "send-report-success-toast",
            SuccessToast,
          )
        } catch (e) {
          const sugarmateError = getSugarmateError(e)

          const ErrorToast = injectProps(
            SendReportErrorToast,
            {
              code: sugarmateError?.code,
              status: sugarmateError?.status,
            },
          )

          showToast(
            "send-report-error-toast",
            ErrorToast,
          )
        }

        setSending(false)
      })()
    },
    [
      analyticsRef,
      currentFollow,
      currentReportRequest,
      sendReport,
      showToast,
    ],
  )

  return (
    <PageTemplate
      user="follow"
      title={polyglot.t("components.page_header.pages.reports")}
      backRoutePath={
        currentFollow.is_self ? "/settings" : "/settings/$accountId"
      }
      backRouteParams={currentFollow.is_self ? {} : { accountId: currentUserId }}
    >
      <Form
        className={styles.container}
        onSubmit={onSubmit}
      >
        <EmailInput
          reportRequest={currentReportRequest}
          updateReportRequest={updateCurrentReportRequest}
        />
        <DateRangeInput
          reportRequest={currentReportRequest}
          updateReportRequest={updateCurrentReportRequest}
        />
        <ShowPhotosToggle
          reportRequest={currentReportRequest}
          updateReportRequest={updateCurrentReportRequest}
        />
        <ActionSubmit
          className={styles.submit}
          stylePreset="primary"
          value={
            sending
              ? polyglot.t(`${polyglotPrefix}sending_cta`)
              : polyglot.t(`${polyglotPrefix}send_cta`)
          }
          disabled={sending}
        />
      </Form>
    </PageTemplate>
  )
}
